import { isPdfAttachment, isWordAttachment } from './clientAttachmentFiles';

export function attachmentMimeType(doc, headerType) {
  const header = String(headerType || '')
    .split(';')[0]
    .trim();
  if (header && header !== 'application/octet-stream') return header;

  const fromDoc = String(doc?.mimeType || '').trim();
  if (fromDoc) return fromDoc;

  if (isPdfAttachment(doc)) return 'application/pdf';

  const name = String(doc?.originalName || doc?.title || '').toLowerCase();
  if (name.endsWith('.doc')) return 'application/msword';
  if (isWordAttachment(doc) || name.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }

  return 'application/octet-stream';
}

export function normalizeAttachmentBlob(data, contentType, doc) {
  const mime = attachmentMimeType(doc, contentType);
  if (data instanceof Blob) {
    if (!data.type || data.type === 'application/octet-stream') {
      return new Blob([data], { type: mime });
    }
    return data;
  }
  return new Blob([data], { type: mime });
}

async function blobLooksLikeApiError(blob) {
  if (!blob || blob.size < 2) return false;
  const head = await blob.slice(0, 80).text();
  const trimmed = head.trimStart();
  return trimmed.startsWith('{') || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html');
}

export async function parseAttachmentDownloadResponse(response, doc) {
  const blob = normalizeAttachmentBlob(
    response.data,
    response.headers?.['content-type'],
    doc
  );

  if (await blobLooksLikeApiError(blob)) {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      throw new Error(parsed.message || 'Не удалось скачать файл');
    } catch (err) {
      if (err instanceof SyntaxError) {
        throw new Error('Не удалось скачать файл');
      }
      throw err;
    }
  }

  return blob;
}

export function suggestedDownloadName(doc) {
  const raw = String(doc?.originalName || doc?.title || 'document').trim();
  if (/\.(pdf|doc|docx)$/i.test(raw)) return raw;
  if (isPdfAttachment(doc)) return `${raw}.pdf`;
  if (isWordAttachment(doc)) return `${raw}.docx`;
  return raw;
}

export function openAttachmentBlob(blob, doc) {
  const fileName = suggestedDownloadName(doc);

  if (isPdfAttachment(doc) || String(blob.type || '').includes('pdf')) {
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    return;
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 120000);
}

export async function fetchAttachmentBlob(downloadBlob, clientId, doc) {
  try {
    return await downloadBlob(clientId, doc.id, doc);
  } catch (err) {
    const status = err.response?.status;
    if (doc?.fileUrl && (status === 404 || status === 403 || status === 502)) {
      const res = await fetch(doc.fileUrl, { credentials: 'same-origin' });
      if (res.ok) {
        const blob = await res.blob();
        return normalizeAttachmentBlob(blob, res.headers.get('content-type'), doc);
      }
    }
    if (status === 404) {
      throw new Error('Файл не найден на сервере — удалите запись и загрузите документ заново');
    }
    const apiMsg = err.response?.data?.message;
    throw new Error(apiMsg || err.message || 'Не удалось скачать файл');
  }
}
